import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Save, Eye, EyeOff, Terminal, Link, Type, ArrowLeft, Check, X, Globe, Languages, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = {
  "en": "English",
  "fr": "Français",
  "fi": "Suomi",
  "de": "Deutsch",
  "ja": "日本語",
  "pt-br": "Português (BR)",
  "es": "Español"
};

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [currentLang, setCurrentLang] = useState("en");
  
  const [settings, setSettings] = useState({
    casino_url: "",
    casino_name: "",
    terminal_lines: ["", "", "", "", ""],
    hero_cta: "",
    features: [
      { title: "", description: "", status: "ACTIVE" },
      { title: "", description: "", status: "LIVE" },
      { title: "", description: "", status: "UNLOCKED" }
    ]
  });

  const getAuthHeaders = () => {
    const auth = localStorage.getItem("admin_auth");
    if (auth) {
      const [user, pass] = atob(auth).split(":");
      return { auth: { username: user, password: pass } };
    }
    return {};
  };

  const loadSettings = async (lang = "en") => {
    try {
      const response = await axios.get(`${API}/settings?lang=${lang}`);
      setSettings({
        casino_url: response.data.casino_url || "",
        casino_name: response.data.casino_name || "",
        terminal_lines: response.data.terminal_lines || ["", "", "", "", ""],
        hero_cta: response.data.hero_cta || "FIND THE GATE",
        features: response.data.features || [
          { title: "", description: "", status: "ACTIVE" },
          { title: "", description: "", status: "LIVE" },
          { title: "", description: "", status: "UNLOCKED" }
        ]
      });
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.get(`${API}/admin/verify`, {
        auth: { username, password }
      });
      setIsAuthenticated(true);
      localStorage.setItem("admin_auth", btoa(`${username}:${password}`));
      await loadSettings("en");
    } catch (err) {
      setError("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await axios.put(`${API}/admin/settings?lang=${currentLang}`, settings, getAuthHeaders());
      setSuccess(`Settings saved for ${LANGUAGES[currentLang]}!`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleTranslate = async (targetLang) => {
    if (targetLang === "en") return;
    
    setError("");
    setSuccess("");
    setTranslating(true);

    try {
      const response = await axios.post(
        `${API}/admin/translate`,
        { target_language: targetLang },
        getAuthHeaders()
      );
      
      if (response.data.success) {
        setSuccess(`Translated to ${LANGUAGES[targetLang]} successfully!`);
        // Reload settings for this language
        if (currentLang === targetLang) {
          await loadSettings(targetLang);
        }
      }
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(`Translation failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTranslating(false);
    }
  };

  const handleLanguageChange = async (lang) => {
    setCurrentLang(lang);
    await loadSettings(lang);
  };

  const handleTerminalLineChange = (index, value) => {
    const newLines = [...settings.terminal_lines];
    newLines[index] = value;
    setSettings({ ...settings, terminal_lines: newLines });
  };

  const handleFeatureChange = (index, field, value) => {
    const newFeatures = [...settings.features];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setSettings({ ...settings, features: newFeatures });
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_auth");
    setIsAuthenticated(false);
    setUsername("");
    setPassword("");
  };

  useEffect(() => {
    const auth = localStorage.getItem("admin_auth");
    if (auth) {
      try {
        const [user, pass] = atob(auth).split(":");
        setUsername(user);
        setPassword(pass);
        axios.get(`${API}/admin/verify`, {
          auth: { username: user, password: pass }
        }).then(() => {
          setIsAuthenticated(true);
          loadSettings("en");
        }).catch(() => {
          localStorage.removeItem("admin_auth");
        });
      } catch {
        localStorage.removeItem("admin_auth");
      }
    }
  }, []);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center px-6">
        <div className="scanlines" />
        <div className="noise" />
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass rounded-sm overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-surface border-b border-border-dim">
              <div className="w-3 h-3 rounded-full bg-neon-red" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-neon-green" />
              <span className="ml-4 terminal-text text-xs">admin@unmarketed ~ </span>
            </div>
            
            <form onSubmit={handleLogin} className="p-8">
              <div className="flex items-center gap-3 mb-8">
                <Lock className="w-6 h-6 text-neon-red" />
                <h1 className="font-heading text-xl font-bold tracking-tight">ADMIN ACCESS</h1>
              </div>

              {error && (
                <div className="mb-6 p-3 bg-neon-red/10 border border-neon-red/30 rounded-sm">
                  <p className="terminal-text text-xs text-neon-red">[ERROR] {error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="terminal-text text-xs text-text-muted block mb-2">USERNAME</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full bg-surface border border-border-dim rounded-sm px-4 py-3 terminal-text text-sm focus:border-neon-red focus:outline-none transition-colors"
                    placeholder="Enter username"
                    data-testid="admin-username"
                  />
                </div>

                <div>
                  <label className="terminal-text text-xs text-text-muted block mb-2">PASSWORD</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-surface border border-border-dim rounded-sm px-4 py-3 pr-12 terminal-text text-sm focus:border-neon-red focus:outline-none transition-colors"
                      placeholder="Enter password"
                      data-testid="admin-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-main transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-8 cyber-btn"
                data-testid="admin-login-btn"
              >
                {loading ? "AUTHENTICATING..." : "LOGIN"}
              </button>

              <a 
                href="/" 
                className="flex items-center justify-center gap-2 mt-6 terminal-text text-xs text-text-muted hover:text-neon-red transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to site
              </a>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-void px-6 py-12">
      <div className="scanlines" />
      <div className="noise" />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Terminal className="w-6 h-6 text-neon-red" />
            <h1 className="font-heading text-2xl font-bold tracking-tight">ADMIN PANEL</h1>
          </div>
          <div className="flex items-center gap-4">
            <a 
              href="/" 
              className="terminal-text text-xs text-text-muted hover:text-neon-red transition-colors flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              View Site
            </a>
            <button
              onClick={handleLogout}
              className="terminal-text text-xs text-text-muted hover:text-neon-red transition-colors"
              data-testid="admin-logout-btn"
            >
              LOGOUT
            </button>
          </div>
        </div>

        {/* Language Selector */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 rounded-sm mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-5 h-5 text-neon-red" />
            <h2 className="font-heading text-lg font-bold">LANGUAGE</h2>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(LANGUAGES).map(([code, name]) => (
              <button
                key={code}
                onClick={() => handleLanguageChange(code)}
                className={`px-4 py-2 terminal-text text-xs rounded-sm border transition-all ${
                  currentLang === code
                    ? 'bg-neon-red text-white border-neon-red'
                    : 'bg-surface border-border-dim hover:border-neon-red'
                }`}
                data-testid={`lang-btn-${code}`}
              >
                {name}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4 pt-4 border-t border-border-dim">
            <Languages className="w-4 h-4 text-text-muted" />
            <span className="terminal-text text-xs text-text-muted">Auto-translate from English:</span>
            <div className="flex flex-wrap gap-2">
              {Object.entries(LANGUAGES).filter(([code]) => code !== "en").map(([code, name]) => (
                <button
                  key={code}
                  onClick={() => handleTranslate(code)}
                  disabled={translating}
                  className="px-3 py-1 terminal-text text-xs bg-surface border border-border-dim rounded-sm hover:border-neon-green hover:text-neon-green transition-all disabled:opacity-50"
                  data-testid={`translate-btn-${code}`}
                >
                  {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : name}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Messages */}
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-neon-red/10 border border-neon-red/30 rounded-sm flex items-center gap-3"
          >
            <X className="w-5 h-5 text-neon-red" />
            <p className="terminal-text text-sm text-neon-red">{error}</p>
          </motion.div>
        )}

        {success && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-neon-green/10 border border-neon-green/30 rounded-sm flex items-center gap-3"
          >
            <Check className="w-5 h-5 text-neon-green" />
            <p className="terminal-text text-sm text-neon-green">{success}</p>
          </motion.div>
        )}

        {/* Current Language Indicator */}
        <div className="mb-6 flex items-center gap-2">
          <span className="terminal-text text-xs text-text-muted">Editing:</span>
          <span className="terminal-text text-xs text-neon-red font-bold">{LANGUAGES[currentLang]}</span>
        </div>

        {/* Settings Cards */}
        <div className="space-y-6">
          {/* Hero CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="cyber-card p-6 rounded-sm"
          >
            <div className="flex items-center gap-3 mb-6">
              <Type className="w-5 h-5 text-neon-red" />
              <h2 className="font-heading text-lg font-bold">HERO CTA</h2>
            </div>

            <div>
              <label className="terminal-text text-xs text-text-muted block mb-2">BUTTON TEXT</label>
              <input
                type="text"
                value={settings.hero_cta}
                onChange={(e) => setSettings({ ...settings, hero_cta: e.target.value })}
                className="w-full max-w-md bg-surface border border-border-dim rounded-sm px-4 py-3 terminal-text text-sm focus:border-neon-red focus:outline-none transition-colors"
                placeholder="FIND THE GATE"
                data-testid="hero-cta-input"
              />
              <p className="terminal-text text-xs text-text-muted mt-2">The main button under the logo</p>
            </div>
          </motion.div>

          {/* Link Settings */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="cyber-card p-6 rounded-sm"
          >
            <div className="flex items-center gap-3 mb-6">
              <Link className="w-5 h-5 text-neon-red" />
              <h2 className="font-heading text-lg font-bold">LINK SETTINGS</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="terminal-text text-xs text-text-muted block mb-2">URL</label>
                <input
                  type="url"
                  value={settings.casino_url}
                  onChange={(e) => setSettings({ ...settings, casino_url: e.target.value })}
                  className="w-full bg-surface border border-border-dim rounded-sm px-4 py-3 terminal-text text-sm focus:border-neon-red focus:outline-none transition-colors"
                  placeholder="https://weezybet.com"
                  data-testid="casino-url-input"
                />
                <p className="terminal-text text-xs text-text-muted mt-2">The URL users will be redirected to</p>
              </div>

              <div>
                <label className="terminal-text text-xs text-text-muted block mb-2">NAME</label>
                <input
                  type="text"
                  value={settings.casino_name}
                  onChange={(e) => setSettings({ ...settings, casino_name: e.target.value })}
                  className="w-full bg-surface border border-border-dim rounded-sm px-4 py-3 terminal-text text-sm focus:border-neon-red focus:outline-none transition-colors"
                  placeholder="WEEZYBET"
                  data-testid="casino-name-input"
                />
                <p className="terminal-text text-xs text-text-muted mt-2">Displayed on the CTA button</p>
              </div>
            </div>
          </motion.div>

          {/* Feature Boxes */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="cyber-card p-6 rounded-sm"
          >
            <div className="flex items-center gap-3 mb-6">
              <Type className="w-5 h-5 text-neon-red" />
              <h2 className="font-heading text-lg font-bold">FEATURE BOXES</h2>
            </div>

            <p className="terminal-text text-xs text-text-muted mb-6">
              Customize the 3 feature boxes displayed on the landing page.
            </p>

            <div className="space-y-8">
              {settings.features.map((feature, index) => (
                <div key={index} className="p-4 bg-surface rounded-sm border border-border-dim">
                  <div className="flex items-center justify-between mb-4">
                    <span className="terminal-text text-xs text-neon-red">BOX {index + 1}</span>
                    <span className="terminal-text text-xs text-neon-green">[{feature.status}]</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="terminal-text text-xs text-text-muted block mb-2">TITLE</label>
                      <input
                        type="text"
                        value={feature.title}
                        onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                        className="w-full bg-void border border-border-dim rounded-sm px-4 py-3 terminal-text text-sm focus:border-neon-red focus:outline-none transition-colors"
                        placeholder="Feature title"
                        data-testid={`feature-title-${index}`}
                      />
                    </div>
                    <div>
                      <label className="terminal-text text-xs text-text-muted block mb-2">DESCRIPTION</label>
                      <input
                        type="text"
                        value={feature.description}
                        onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                        className="w-full bg-void border border-border-dim rounded-sm px-4 py-3 terminal-text text-sm focus:border-neon-red focus:outline-none transition-colors"
                        placeholder="Feature description"
                        data-testid={`feature-desc-${index}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Terminal Text */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="cyber-card p-6 rounded-sm"
          >
            <div className="flex items-center gap-3 mb-6">
              <Type className="w-5 h-5 text-neon-red" />
              <h2 className="font-heading text-lg font-bold">TERMINAL TEXT</h2>
            </div>

            <p className="terminal-text text-xs text-text-muted mb-6">
              Customize the terminal window text. Each line appears sequentially.
            </p>

            <div className="space-y-4">
              {settings.terminal_lines.map((line, index) => (
                <div key={index}>
                  <label className="terminal-text text-xs text-text-muted block mb-2">
                    LINE {index + 1}
                  </label>
                  <input
                    type="text"
                    value={line}
                    onChange={(e) => handleTerminalLineChange(index, e.target.value)}
                    className="w-full bg-surface border border-border-dim rounded-sm px-4 py-3 terminal-text text-sm focus:border-neon-red focus:outline-none transition-colors"
                    placeholder={`Terminal line ${index + 1}`}
                    data-testid={`terminal-line-${index}`}
                  />
                </div>
              ))}
            </div>

            {/* Preview */}
            <div className="mt-8">
              <label className="terminal-text text-xs text-text-muted block mb-3">PREVIEW</label>
              <div className="glass rounded-sm overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2 bg-surface border-b border-border-dim">
                  <div className="w-2 h-2 rounded-full bg-neon-red" />
                  <div className="w-2 h-2 rounded-full bg-yellow-500" />
                  <div className="w-2 h-2 rounded-full bg-neon-green" />
                </div>
                <div className="p-4">
                  {settings.terminal_lines.map((line, index) => (
                    <p key={index} className="terminal-text text-xs mb-1">
                      {line.includes("[SUCCESS]") ? (
                        <><span className="text-neon-green">[SUCCESS]</span>{line.replace("[SUCCESS]", "")}</>
                      ) : line.includes("[INFO]") ? (
                        <><span className="text-text-muted">[INFO]</span>{line.replace("[INFO]", "")}</>
                      ) : line.startsWith("$") ? (
                        <><span className="text-neon-red">$</span>{line.substring(1)}</>
                      ) : (
                        line
                      )}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Save Button */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-8 flex justify-end"
        >
          <button
            onClick={handleSave}
            disabled={loading}
            className="cyber-btn flex items-center gap-3"
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4" />
            {loading ? "SAVING..." : `SAVE ${LANGUAGES[currentLang].toUpperCase()}`}
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default Admin;
